<template>
    <section class="order-date-time-form" :class="{ shown: isInitialized }">
        <div v-if="isInitialized">
            <div>
                <label for="order-date">
                    {{ translations.general.date }}
                </label>
            </div>
            <OrderDatePicker
                v-model="selectedDate"
                :minDate="minDateToLoad"
                :maxDate="maxDateToLoad"
                :disabledDates="disabledDates"
                inputId="order-date"
            />
        </div>

        <div v-if="isInitialized">
            <div>
                <label for="order-time">
                    {{ translations.general.time }}
                </label>
            </div>
            <OrderTimeDropdown
                v-model="selectedTime"
                :options="timeSlots"
                :disabled="!selectedDate"
                inputId="order-time"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type { IFulfillmentDateSlotDto, SelectedOrderDateAndTime } from "@/data/model/Customer";
    import type { IService } from "@/data/model/Service";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { DateTime } from "@/data/util/DateTime";
    import { translations } from "@/shared/localization/translations";
    import { watchIgnorable } from "@vueuse/core";
    import { computed, ref, watch } from "vue";

    import OrderDatePicker from "@/components/preorder/OrderDatePicker.vue";
    import OrderTimeDropdown from "@/components/preorder/OrderTimeDropdown.vue";

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    // define props
    const props = defineProps<{
        modelValue: SelectedOrderDateAndTime | null;
    //    orderServiceId: string | undefined | null;
    }>();

    const tableAllocation = useTableAllocationStore();

    // current service
    const currentService = computed<IService | null>(() => {
        return tableAllocation.allocation?.services.find((service) => service.id === selectedServiceId.value) ?? null;
    });

    // define emits
    const emit = defineEmits(["update:modelValue"]);

    // define state values
    const selectedServiceId = ref<string | null>(null);
    const isInitialized = ref<boolean>(false);
    const selectedDate = ref<Date | null>(null);
    const selectedTime = ref<string | null>(null);
    const dateSlots = ref<IFulfillmentDateSlotDto[]>([]);

    const minDateToLoad = computed(() => getToday());
    const maxDateToLoad = computed(() => getMaxDate());

    const disabledDates = computed(() => {
        const result = [] as Date[];
        for (
            let dateToCheck = minDateToLoad.value;
            dateToCheck <= maxDateToLoad.value;
            dateToCheck = addDays(dateToCheck, 1)
        ) {
            if (getSlotByDate(dateToCheck) == null) {
                result.push(dateToCheck);
            }
        }

        return result;
    });

    const selectedDateSlot = computed(() => {
        if (selectedDate.value == null) {
            return null;
        }

        const slotByDate = getSlotByDate(selectedDate.value);
        return slotByDate;
    });

    const timeSlots = computed(() => selectedDateSlot.value?.timeSlots ?? []);

    const currentSelectedDateAndTime = computed(() => {
        return {
            date: selectedDate.value,
            time: selectedTime.value,
        };
    });

    // if selected date in calendar changes - clear selected time
    const ignorable = watchIgnorable(selectedDate, () => {
        updateSelection(selectedDate.value);
    });

    function updateSelection(selectedDate: Date | null) {
        const selectedDateSlot = selectedDate != null ? getSlotByDate(selectedDate) : null;

        // If there is no selected date slot, then deselect time slot
        if (selectedDateSlot == null) {
            selectedTime.value = null;
            return;
        }

        // Check if time slot is selected
        const selectedTimeStr = selectedTime.value;
        if (selectedTimeStr != null && selectedTimeStr !== "") {
            //check if selected time exists in the new selected date slot
            const selectedTimeSlot = selectedDateSlot.timeSlots.find((t) => t.startTime === selectedTime.value);
            if (selectedTimeSlot != null) {
                selectedTime.value = selectedTimeSlot.startTime;
                return;
            } else {
                // not available on that date, select the first available time after the set time.
                const availableTimes = selectedDateSlot.timeSlots.map((t) => DateTime.toDate(t.startTime));
                const time = DateTime.toDate(selectedTimeStr);

                const newTimeAfterSelectedIndex = availableTimes.findIndex((t) => t >= time);
                if (newTimeAfterSelectedIndex != -1) {
                    selectedTime.value = selectedDateSlot.timeSlots[newTimeAfterSelectedIndex].startTime;
                    return;
                }
            }
        }

        // Select the nearest available time if possible
        if (selectedDateSlot.timeSlots.length > 0) {
            selectedTime.value = selectedDateSlot.timeSlots[0].startTime;
            return;
        }

        selectedTime.value = null;
    }

    // define functions
    async function initialize(
        currentSelection: SelectedOrderDateAndTime | null,
        orderServiceId: string | null | undefined,
        showPopupOnParent: JQuery | null) {
        selectedServiceId.value = orderServiceId === undefined ? null : orderServiceId;

        // load fulfillment slots and add them to dateSlots
        const fulfillmentSlots = await customerDao.getFulfillmentSlots({
            orderServiceId: selectedServiceId.value == null ? undefined : selectedServiceId.value,
            startDate: minDateToLoad.value,
        });
        dateSlots.value = [];
        dateSlots.value.push(...fulfillmentSlots);

        if (fulfillmentSlots.length === 0) {
            selectedDate.value = null;
            selectedTime.value = null;

            let parent = showPopupOnParent != null ? showPopupOnParent : $(".app");
            Popup.show({
                message: translations.pay.noFulfillmentSlotAvailableV2.replace(
                    "{0}",
                    tableAllocation.allocation?.locationName ?? ""
                ),
                uniqueId: "no-fulfillment-slot-available-popup",
            }, parent);
        } else if (currentSelection != null) {
            // if we are editing existing selection - initialize selectedDate and selectedTime
            ignorable.ignoreUpdates(() => {
                const selectedDateSlot = currentSelection.date != null ? getSlotByDate(currentSelection.date) : null;
                if (selectedDateSlot != null) {
                    selectedDate.value = currentSelection.date;
                    selectedDate.value.setHours(0, 0, 0, 0); // todo: hack to make it work, because of timezone issues
                    selectedTime.value = currentSelection.time;
                } else {
                    const availableClosestDateSlot = dateSlots.value.find((d) => d.isAvailable === true);
                    if (availableClosestDateSlot != null) {
                        selectedDate.value = availableClosestDateSlot.date;
                        selectedDate.value.setHours(0, 0, 0, 0);
                    }
                }

                updateSelection(selectedDate.value);
            });
        } else {
            const availableClosestDateSlot = dateSlots.value.find((d) => d.isAvailable === true);
            if (availableClosestDateSlot != null) {
                selectedDate.value = availableClosestDateSlot.date;
                if (availableClosestDateSlot.timeSlots.length > 0) {
                    selectedTime.value = availableClosestDateSlot.timeSlots[0].startTime;
                }
            }
        }

        isInitialized.value = true;
    }


    function reset() {
        dateSlots.value = [];
        selectedDate.value = null;
        selectedTime.value = null;

        isInitialized.value = false;
    }

    function getToday() {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }

    // TODO: Migrate the whole logic to backend
    function getMaxDate() {
        const maxPreorderPeriod = currentService.value?.useCase?.maxPreorderPeriod ?? 93;
        // todo: lazy load next months
        if (dateSlots.value.length == 0) {
            const maxDate = getToday();
            maxDate.setDate(maxDate.getDate() + maxPreorderPeriod);
            return maxDate;
        } else {
            const maxDate = dateSlots.value[dateSlots.value.length - 1].date;
            return maxDate;
        }
    }

    function addDays(date: Date, days: number) {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    function getSlotByDate(date: Date): IFulfillmentDateSlotDto | undefined {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0);
        return dateSlots.value.find((x) => {
            let zeroDate = x.date;
            zeroDate.setHours(0, 0, 0);
            return zeroDate.getTime() == newDate.getTime();
        });
    }

    // event handlers
    watch(currentSelectedDateAndTime, (newValue) => {
        emit("update:modelValue", newValue);
    });

    // update watchers
    watch(
        () => props.modelValue,
        (newValue) => {
            selectedDate.value = newValue?.date ?? null;
            selectedTime.value = newValue?.time ?? null;
        }
    );

    // define exposed
    defineExpose({
        initialize,
        reset
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .order-date-time-form {
        opacity: 0;
        transition: opacity 0.4s variables.$easeOutExpo;

        &.shown {
            opacity: 1;
        }

        label {
            //margin-top: 1em;
            font-weight: bold;
            margin-bottom: 0.5em;
            display: inline-block;
        }

        > div:nth-child(2) {
            margin-top: 2em;
        }
    }
</style>
